import {useEffect, useRef, useState, memo} from 'react';
import {MediaDetection, MediaDetectionType, Rect as IRect} from '@legit-health/ui';
import Konva from 'konva';
import {KonvaEventObject} from 'konva/lib/Node';
import {Rect, Group, Transformer} from 'react-konva';
import DeleteAnchor from './DeleteAnchor';
import detectionColors from './consts/detectionColors';
import boundBoxFunc from './utils/boundBoxFunc';
import calculateRect from './utils/calculateRect';
import scaleRect from './utils/scaleRect';

function DetectionRect({
  onDelete,
  detection,
  scaleFactor,
  onDragDetectionEnd,
  onSelect,
  isSelected,
  onTransformDetection
}: {
  detection: MediaDetection;
  scaleFactor: number;
  onDelete: (detection: MediaDetection) => void;
  onDragDetectionEnd: (detection: MediaDetection, rect: IRect) => void;
  onTransformDetection: (detection: MediaDetection, rect: IRect) => void;
  onSelect: (detection: MediaDetection | null) => void;
  isSelected: boolean;
}) {
  const {rect: detectionRect, type} = detection;
  const groupRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const [transformedRect, setTransformedRect] = useState<IRect>(detectionRect);

  useEffect(
    function () {
      const transformer = transformerRef.current;
      const rect = groupRef.current;
      if (isSelected && transformer && rect) {
        transformer.nodes([rect]);
        transformer.getLayer()?.batchDraw();
      }
    },
    [isSelected]
  );

  function handleDragDetectionStart(event: KonvaEventObject<DragEvent>) {
    event.cancelBubble = true;
  }

  function handleTransformEnd(event: KonvaEventObject<Event>) {
    event.cancelBubble = true;
    const group = groupRef.current;
    if (!group) {
      return;
    }
    onTransformDetection(detection, calculateRect(group, scaleFactor));
  }

  function handleTransform(event: KonvaEventObject<Event>) {
    event.cancelBubble = true;
    const group = groupRef.current;
    if (!group) {
      return;
    }
    setTransformedRect(calculateRect(group, scaleFactor));
  }

  function handleDragEnd(event: KonvaEventObject<DragEvent>) {
    event.cancelBubble = true;
    onDragDetectionEnd(detection, {
      ...detection.rect,
      topLeft: {
        x: event.target.x() / scaleFactor,
        y: event.target.y() / scaleFactor
      }
    });
  }

  const scaledRect = scaleRect(transformedRect, scaleFactor);

  function handleClick() {
    onSelect(detection);
  }

  return (
    <>
      <Group
        width={scaledRect.width}
        height={scaledRect.height}
        x={scaledRect.topLeft.x}
        y={scaledRect.topLeft.y}
        draggable
        onDragStart={handleDragDetectionStart}
        onDragEnd={handleDragEnd}
        onTransformEnd={handleTransformEnd}
        onTransform={handleTransform}
        ref={groupRef}
      >
        <Rect
          x={0}
          y={0}
          width={scaledRect.width}
          height={scaledRect.height}
          fill="transparent"
          stroke={detectionColors[type as keyof typeof detectionColors]}
          strokeWidth={detection.type === MediaDetectionType.Treatment ? 4 : 2}
          fillEnabled={false}
          strokeScaleEnabled={false}
        />
        <Rect
          x={0}
          y={0}
          width={scaledRect.width}
          height={scaledRect.height}
          fill="transparent"
          stroke="transparent"
          strokeWidth={15}
          fillEnabled={false}
          onClick={handleClick}
          onTap={handleClick}
          strokeScaleEnabled={false}
        />

        {!isSelected && (
          <DeleteAnchor x={scaledRect.width} y={0} onDelete={() => onDelete(detection)} />
        )}
      </Group>
      {isSelected && <Transformer ref={transformerRef} boundBoxFunc={boundBoxFunc} ignoreStroke />}
    </>
  );
}

export default memo(DetectionRect);
